import { Dayjs } from "dayjs";

import { isPhaseEnabled, phaseSlotLabel } from "../../../../constants/ripeningPhaseSlots";
import { ethyleneDosingFor, formatProgramFieldValue, programFieldLabel } from "../../../../constants/ripeningProgramFields";
import {
    isStorageDefinition,
    StoreBlockFieldKey,
    storeFieldLabel,
    storeValuesOf,
    StoreValues,
    validateStoreValues,
    withDerivedStoreEthylene,
} from "../../../../constants/ripeningStorePrograms";
import { RipeningRoomRun, RipewisePhase } from "../../../../hooks/useRipeningPrograms";
import { dayjs } from "../../../../utils/date";
import { getCycleStartCommand, RipeningProgramSnapshot } from "./ripeningProgramCycle";

// * Same bounds as the program form and the backend.
export const MIN_TEMPERATURE = -10;
export const MAX_TEMPERATURE = 45;

// * The ripening room prefixes the program name it runs; users never see that prefix.
const PROGRAM_NAME_PREFIX = /^\s*ripewise\s*-\s*/i;

export type PhaseStatus = "done" | "active" | "future";

/** Allowed changes to one phase: setpoints, time, and switching a phase that has not started on or off. */
export interface PhaseDraft {
    t_end?: number;
    duration_hours?: number;
    ethylene_setpoint?: number;
    // * Never set by hand: follows the C2H4 setpoint of a changed phase (see updateDraft).
    ethylene_active?: boolean;
    enabled?: boolean;
}

export type ProgramDraft = Record<number, PhaseDraft>;

export interface ScheduledPhase {
    phase: number;
    status: PhaseStatus;
    // * A disabled phase is part of the program but does not run (and takes no time) unless switched on.
    enabled: boolean;
    startAt: Dayjs;
    endAt: Dayjs;
    durationHours: number;
    tEnd: number | null;
    ethyleneSetpoint: number | null;
    ethyleneActive: boolean;
}

export interface ProgramSchedule {
    phases: ScheduledPhase[];
    startAt: Dayjs;
    endAt: Dayjs;
    currentPhase: number;
    // * Hours the active phase has run so far.
    elapsedInCurrentHours: number;
    // False when the live room phase differs from the schedule and no phase-start time is available.
    currentPhaseTimingReliable: boolean;
    // * Overall progress 0..100.
    progress: number;
}

/** Allowed changes to a storage program while the room stores. */
export interface StoreEditPayload {
    setpoint?: number;
    air_offset?: number;
    ventilation?: number;
    ethylene_setpoint?: number;
    co2_setpoint?: number;
    o2_setpoint?: number;
    scrubber_active?: boolean;
    n2_active?: boolean;
}

/** Allowed changes to a ripening program's general settings while the room ripens (whole minutes). */
export interface GeneralEditPayload {
    ventilation_period?: number;
    ventilation_interval?: number;
}

export interface ProgramEditPayload {
    phases: Array<{ phase: number } & PhaseDraft>;
    proceed_to_next_phase?: boolean;
    store?: StoreEditPayload;
    general?: GeneralEditPayload;
}

const finiteNumber = (value: unknown): number | null => (
    typeof value === "number" && Number.isFinite(value) ? value : null
);

/** Cycle dates are stored as UTC without an offset; show them in local time. */
export function parseCycleDate(value: string | null | undefined): Dayjs | null {
    if (!value) return null;
    const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(value.trim());
    const parsed = hasOffset ? dayjs(value) : dayjs.utc(value).local();
    return parsed.isValid() ? parsed : null;
}

/** The program a cycle was started with (meta.ripening_program), when it has phases or is a storage program. */
export function getCycleProgram(meta: any): RipeningProgramSnapshot | null {
    let program = meta?.ripening_program;
    if (typeof program === "string") {
        if (!program.trim().startsWith("{")) return null;
        try {
            program = JSON.parse(program);
        } catch {
            return null;
        }
    }
    if (!program || typeof program !== "object") return null;
    const phases = program.definition?.ripewise?.phases;
    if (Array.isArray(phases) && phases.length > 0) return program as RipeningProgramSnapshot;
    // * A storage program (ripewise with only a store) has no phases.
    return program.definition?.mode === "ripewise" && isStorageDefinition(program.definition) ? program as RipeningProgramSnapshot : null;
}

/** The store settings of the cycle's storage program; null when the cycle runs a ripening program. */
export function getCycleStore(meta: any): StoreValues | null {
    const program = getCycleProgram(meta);
    return program && isStorageDefinition(program.definition) ? storeValuesOf(program.definition) : null;
}

/** A cycle whose program can be changed: started with a program on a linked ripening room. */
function hasStartedControllerProgram(cycle: any): boolean {
    const command = getCycleStartCommand(cycle);
    if (!command) return false;
    // Status-less commands predate command tracking and belong to already-running cycles.
    return command.status === undefined || command.status === "accepted";
}

export function isEditableProgramCycle(cycle: any): boolean {
    return !!cycle?.active && hasStartedControllerProgram(cycle) && !!getCycleProgram(cycle.meta);
}

/**
 * A gateway-backed room whose active cycle was started with a program on the controller:
 * its conditions are managed through Edit cycle -> Program, not per-day setpoints.
 */
export function runsControllerProgram(location: any, cycle: any): boolean {
    return !!location?.gateway && !!cycle?.active && hasStartedControllerProgram(cycle);
}

export function getProgramDisplayName(program: Pick<RipeningProgramSnapshot, "name" | "id"> | null | undefined): string {
    const name = typeof program?.name === "string" ? program.name.replace(PROGRAM_NAME_PREFIX, "").trim() : "";
    return name || String(program?.id ?? "Program");
}

export function sortedPhases(program: RipeningProgramSnapshot): RipewisePhase[] {
    return [...(program.definition.ripewise?.phases ?? [])].sort((a, b) => a.phase - b.phase);
}

/** Phase number the ripening room reports, when it names a phase of this program. */
export function getLivePhaseNumber(run: RipeningRoomRun | null | undefined, phases: RipewisePhase[]): number | null {
    if (!run) return null;
    let value: unknown = run.phase_number;
    if ((value === null || value === undefined) && typeof run.phase === "string" && /^\s*\d+\s*$/.test(run.phase)) {
        value = Number(run.phase);
    }
    if (typeof run.phase === "number" && (value === null || value === undefined)) value = run.phase;
    return typeof value === "number" && phases.some((phase) => phase.phase === value) ? value : null;
}

/** Name of the active phase as the ripening room reports it (never a bare number). */
export function getLivePhaseName(run: RipeningRoomRun | null | undefined): string | null {
    if (typeof run?.phase !== "string") return null;
    const name = run.phase.trim();
    return name && !/^\d+$/.test(name) ? name : null;
}

const phaseDuration = (phase: RipewisePhase, draft?: PhaseDraft) => {
    const value = draft?.duration_hours ?? finiteNumber(phase.values.TimeSpan);
    return value !== null && value > 0 ? value : 0;
};

const phaseRuns = (phase: RipewisePhase, draft?: PhaseDraft) => draft?.enabled ?? isPhaseEnabled(phase);

function computedCurrentPhase(phases: RipewisePhase[], elapsedHours: number): number {
    const running = phases.filter((phase) => phaseRuns(phase));
    const ordered = running.length > 0 ? running : phases;
    let cumulative = 0;
    const current = ordered.find((phase) => {
        cumulative += phaseDuration(phase);
        return elapsedHours < cumulative;
    });
    return (current ?? ordered[ordered.length - 1]).phase;
}

/**
 * Start/end time and status of every phase, from the cycle start and the phase durations
 * (with the draft applied). The active phase comes from the ripening room when it reports
 * one, otherwise from the clock.
 */
export function buildProgramSchedule(
    program: RipeningProgramSnapshot,
    startAt: Dayjs,
    now: Dayjs,
    livePhase: number | null,
    draft: ProgramDraft = {},
): ProgramSchedule {
    const phases = sortedPhases(program);
    const elapsedHours = now.diff(startAt, "minute") / 60;
    const computedPhase = computedCurrentPhase(phases, elapsedHours);
    const hasKnownLivePhase = livePhase !== null && phases.some(({ phase }) => phase === livePhase);
    const currentPhase = livePhase !== null && hasKnownLivePhase ? livePhase : computedPhase;
    const currentPhaseTimingReliable = !hasKnownLivePhase || livePhase === computedPhase;

    let offsetHours = 0;
    let currentStartHours = 0;
    const scheduled = phases.map((phase): ScheduledPhase => {
        const phaseDraft = draft[phase.phase];
        const durationHours = phaseDuration(phase, phaseDraft);
        const enabled = phaseRuns(phase, phaseDraft);
        let status: PhaseStatus = "future";
        if (phase.phase < currentPhase) status = "done";
        else if (phase.phase === currentPhase) status = "active";
        if (status === "active") currentStartHours = offsetHours;
        // * A phase that does not run takes no time: it sits at the point where it would start.
        const runHours = enabled ? durationHours : 0;
        const item: ScheduledPhase = {
            phase: phase.phase,
            status,
            enabled,
            startAt: startAt.add(offsetHours * 60, "minute"),
            endAt: startAt.add((offsetHours + runHours) * 60, "minute"),
            durationHours,
            tEnd: phaseDraft?.t_end ?? finiteNumber(phase.values.T_End),
            ethyleneSetpoint: phaseDraft?.ethylene_setpoint ?? finiteNumber(phase.values.Spv_C2H4),
            // * Ethylene dosing follows the setpoint.
            ethyleneActive: ethyleneDosingFor(phaseDraft?.ethylene_setpoint ?? finiteNumber(phase.values.Spv_C2H4)) === 1,
        };
        offsetHours += runHours;
        return item;
    });

    const totalHours = offsetHours;
    return {
        phases: scheduled,
        startAt,
        endAt: startAt.add(totalHours * 60, "minute"),
        currentPhase,
        elapsedInCurrentHours: currentPhaseTimingReliable ? Math.max(0, elapsedHours - currentStartHours) : 0,
        currentPhaseTimingReliable,
        progress: totalHours > 0 ? Math.min(100, Math.max(0, Math.round((elapsedHours / totalHours) * 100))) : 0,
    };
}

export interface SkipAvailability {
    enabled: boolean;
    reason: string | null;
}

/** The next phase that will run after the current one, if any. */
export function nextRunningPhase(schedule: ProgramSchedule): ScheduledPhase | undefined {
    return schedule.phases.find((phase) => phase.phase > schedule.currentPhase && phase.enabled);
}

/**
 * Whether "Skip to next phase" can be used now. The skipped phase is recorded as the whole
 * hours it ran, which come from the room's own phase progress (phaseProgress, %), or else
 * from the clock while the room is where the schedule expects it.
 */
export function getSkipAvailability(
    schedule: ProgramSchedule,
    roomStatusAvailable: boolean,
    hasUnsavedChanges: boolean,
    phaseProgress: number | null = null,
): SkipAvailability {
    if (!roomStatusAvailable) return { enabled: false, reason: "The ripening room can't be reached right now." };
    if (!nextRunningPhase(schedule)) return { enabled: false, reason: "This is the last phase." };
    if (!schedule.currentPhaseTimingReliable && phaseProgress === null) return { enabled: false, reason: "The current phase start time is unavailable." };
    if (hasUnsavedChanges) return { enabled: false, reason: "Save or undo your changes first." };
    return { enabled: true, reason: null };
}

/** Whether a phase may be switched on or off now: only before it starts. */
export function getPhaseSwitchAvailability(phase: ScheduledPhase): SkipAvailability {
    if (phase.status === "active") return { enabled: false, reason: "Already started" };
    if (phase.status === "done") return { enabled: false, reason: "Already passed" };
    return { enabled: true, reason: null };
}

export function getSkipConfirmation(schedule: ProgramSchedule, phaseName: string | null, phaseProgress: number | null = null): string {
    const next = nextRunningPhase(schedule);
    const current = `Phase ${schedule.currentPhase}${phaseName ? ` (${phaseName})` : ""}`;
    const nextPhase = next?.phase ?? schedule.currentPhase + 1;
    // * A phase runs at least 1 hour: a skip in its first hour ends it once that hour is up.
    const active = schedule.phases.find((phase) => phase.phase === schedule.currentPhase);
    // * The room's own phase progress leads, as for the hours recorded.
    const hoursDone = phaseProgress !== null && active ? (phaseProgress / 100) * active.durationHours : schedule.elapsedInCurrentHours;
    if (hoursDone < 1 && active) {
        return `${current} ends at ${active.startAt.add(1, "hour").format("HH:mm")}, after its first hour, and phase ${nextPhase} starts then. This cannot be undone.`;
    }
    return `${current} ends now and phase ${nextPhase} starts. The ripening room will follow immediately. This cannot be undone.`;
}

/**
 * Set one draft value; a value equal to the program's own value is removed from the draft.
 * A changed phase also gets ethylene dosing to match its C2H4 setpoint when the program's own
 * mode does not (also when the program was saved with a mode that did not match).
 */
export function updateDraft(
    draft: ProgramDraft,
    program: RipeningProgramSnapshot,
    phaseNumber: number,
    key: keyof PhaseDraft,
    value: number | boolean | undefined,
): ProgramDraft {
    const phase = sortedPhases(program).find((item) => item.phase === phaseNumber);
    if (!phase) return draft;
    const original: Record<keyof PhaseDraft, unknown> = {
        t_end: finiteNumber(phase.values.T_End),
        duration_hours: finiteNumber(phase.values.TimeSpan),
        ethylene_setpoint: finiteNumber(phase.values.Spv_C2H4),
        ethylene_active: !!phase.values.Modus_C2H4,
        enabled: isPhaseEnabled(phase),
    };
    const nextPhase: PhaseDraft = { ...draft[phaseNumber] };
    if (value === undefined || value === original[key]) delete nextPhase[key];
    else (nextPhase as Record<string, unknown>)[key] = value;
    delete nextPhase.ethylene_active;
    if (Object.keys(nextPhase).length > 0) {
        const active = ethyleneDosingFor(nextPhase.ethylene_setpoint ?? original.ethylene_setpoint) === 1;
        if (active !== original.ethylene_active) nextPhase.ethylene_active = active;
    }
    const next = { ...draft };
    if (Object.keys(nextPhase).length === 0) delete next[phaseNumber];
    else next[phaseNumber] = nextPhase;
    return next;
}

/** Problems that keep the draft from being sent; empty when it is valid. */
export function validateDraft(draft: ProgramDraft, schedule: ProgramSchedule): string[] {
    const errors: string[] = [];
    Object.entries(draft).forEach(([phaseKey, values]) => {
        const phase = Number(phaseKey);
        if (phase < schedule.currentPhase) errors.push(`Phase ${phase} is done and can't be changed.`);
        else if (values.enabled !== undefined && phase === schedule.currentPhase) {
            errors.push(`${phaseSlotLabel(phase)} has already started and can't be switched ${values.enabled ? "on" : "off"}.`);
        }
        if (values.t_end !== undefined && !(values.t_end >= MIN_TEMPERATURE && values.t_end <= MAX_TEMPERATURE)) {
            errors.push(`Phase ${phase}: end temperature must be between ${MIN_TEMPERATURE} and ${MAX_TEMPERATURE} °C.`);
        }
        if (values.duration_hours !== undefined) {
            if (!Number.isInteger(values.duration_hours) || values.duration_hours < 1) {
                errors.push(`Phase ${phase}: duration must be at least 1 whole hour.`);
            } else if (phase === schedule.currentPhase && !schedule.currentPhaseTimingReliable) {
                errors.push(`Phase ${phase}: duration cannot be changed because its start time is unavailable.`);
            } else if (phase === schedule.currentPhase && values.duration_hours <= schedule.elapsedInCurrentHours) {
                errors.push(`Phase ${phase} has already run for ${Math.floor(schedule.elapsedInCurrentHours)} h. To end it now, use Skip to next phase.`);
            }
        }
        if (values.ethylene_setpoint !== undefined && !(values.ethylene_setpoint >= 0)) {
            errors.push(`Phase ${phase}: ethylene must be 0 or more.`);
        }
    });
    return errors;
}

export function buildProgramEditPayload(draft: ProgramDraft, generalDraft: GeneralDraft = {}): ProgramEditPayload {
    const payload: ProgramEditPayload = {
        phases: Object.entries(draft)
            .map(([phase, values]) => ({ phase: Number(phase), ...values }))
            .filter((item) => Object.keys(item).length > 1)
            .sort((a, b) => a.phase - b.phase),
    };
    const general = buildGeneralEditPayload(generalDraft);
    if (general) payload.general = general;
    return payload;
}

// * General settings a running ripening program may change: the ventilation timer.
export const EDITABLE_GENERAL_KEYS = ["Aereation_Period", "Aereation_Interval"] as const;
export type EditableGeneralKey = typeof EDITABLE_GENERAL_KEYS[number];
export type GeneralDraft = Partial<Record<EditableGeneralKey, number>>;

const GENERAL_PAYLOAD_FIELDS: Record<EditableGeneralKey, keyof GeneralEditPayload> = {
    Aereation_Period: "ventilation_period",
    Aereation_Interval: "ventilation_interval",
};

/** The running program's general value for a key, if it has one. */
export const generalValue = (program: RipeningProgramSnapshot, key: EditableGeneralKey): number | null => (
    finiteNumber(program.definition?.ripewise?.general?.[key])
);

/** Set one general value; a value equal to the running one is removed from the draft. */
export function updateGeneralDraft(draft: GeneralDraft, program: RipeningProgramSnapshot, key: EditableGeneralKey, value: number | undefined): GeneralDraft {
    const next = { ...draft };
    if (value === undefined || value === generalValue(program, key)) delete next[key];
    else next[key] = value;
    return next;
}

/** Problems that keep the general draft from being sent; empty when it is valid. */
export function validateGeneralDraft(draft: GeneralDraft): string[] {
    return (Object.entries(draft) as Array<[EditableGeneralKey, number]>)
        .filter(([, value]) => !Number.isInteger(value) || value < 0)
        .map(([key]) => `${programFieldLabel(key)} must be a whole number of minutes, 0 or more.`);
}

function buildGeneralEditPayload(draft: GeneralDraft): GeneralEditPayload | null {
    const entries = Object.entries(draft) as Array<[EditableGeneralKey, number]>;
    if (entries.length === 0) return null;
    const general: GeneralEditPayload = {};
    entries.forEach(([key, value]) => { general[GENERAL_PAYLOAD_FIELDS[key]] = value; });
    return general;
}

/** Plain-language lines describing what the general draft changes. */
export function describeGeneralDraft(draft: GeneralDraft, program: RipeningProgramSnapshot): string[] {
    return EDITABLE_GENERAL_KEYS
        .filter((key) => draft[key] !== undefined)
        .map((key) => `${programFieldLabel(key)}: ${formatNumber(generalValue(program, key))} → ${draft[key]}`);
}

const formatNumber = (value: number | null | undefined) => (value === null || value === undefined ? "-" : String(value));
export const formatDateTime = (value: Dayjs) => value.format("ddd D MMM HH:mm");

/** Plain-language lines describing what the draft changes. */
export function describeDraft(draft: ProgramDraft, program: RipeningProgramSnapshot, before: ProgramSchedule, after: ProgramSchedule): string[] {
    const phases = sortedPhases(program);
    const lines: string[] = [];
    Object.entries(draft).sort(([a], [b]) => Number(a) - Number(b)).forEach(([phaseKey, values]) => {
        const phase = phases.find((item) => item.phase === Number(phaseKey));
        if (!phase) return;
        if (values.enabled !== undefined) {
            lines.push(`${phaseSlotLabel(phase.phase)} will be switched ${values.enabled ? "on" : "off"}`);
        }
        if (values.t_end !== undefined) {
            lines.push(`Phase ${phase.phase}: end temperature ${formatNumber(finiteNumber(phase.values.T_End))} → ${values.t_end} °C`);
        }
        if (values.duration_hours !== undefined) {
            lines.push(`Phase ${phase.phase}: duration ${formatNumber(finiteNumber(phase.values.TimeSpan))} → ${values.duration_hours} h`);
        }
        if (values.ethylene_active !== undefined) {
            lines.push(`Phase ${phase.phase}: ethylene dosing ${values.ethylene_active ? "on" : "off"}`);
        }
        if (values.ethylene_setpoint !== undefined) {
            lines.push(`Phase ${phase.phase}: C2H4 dosage ${formatNumber(finiteNumber(phase.values.Spv_C2H4))} → ${values.ethylene_setpoint} ppm`);
        }
    });
    if (lines.length > 0 && !before.endAt.isSame(after.endAt)) {
        lines.push(`Program ends ${formatDateTime(after.endAt)} (was ${formatDateTime(before.endAt)})`);
    }
    return lines;
}

// * Store settings changed while the room stores, by the room's store field.
export type StoreDraft = Partial<Record<StoreBlockFieldKey, number>>;

/** The store settings with the draft applied, ethylene dosing following its setpoint. */
export const draftedStore = (store: StoreValues, draft: StoreDraft): StoreValues => {
    const merged: StoreValues = { ...store };
    Object.entries(draft).forEach(([key, value]) => {
        if (value !== undefined) merged[key] = value;
    });
    return Object.keys(draft).length > 0 ? withDerivedStoreEthylene(merged) : merged;
};

/** Set one store value; a value equal to the running one is removed from the draft. */
export function updateStoreDraft(draft: StoreDraft, store: StoreValues, key: StoreBlockFieldKey, value: number | undefined): StoreDraft {
    const next = { ...draft };
    if (value === undefined || value === store[key]) delete next[key];
    else next[key] = value;
    return next;
}

/** Problems that keep the store draft from being sent; empty when it is valid. */
export function validateStoreDraft(draft: StoreDraft, store: StoreValues): string[] {
    if (Object.keys(draft).length === 0) return [];
    const error = validateStoreValues(draftedStore(store, draft));
    return error ? [error] : [];
}

const STORE_PAYLOAD_FIELDS: Record<StoreBlockFieldKey, keyof StoreEditPayload> = {
    Store_Setpoint: "setpoint",
    Store_AirOffset: "air_offset",
    Store_Mode_Aereation: "ventilation",
    Store_Spv_C2H4: "ethylene_setpoint",
    Store_Spv_Co2: "co2_setpoint",
    Store_Spv_O2: "o2_setpoint",
    Store_Mode_Scrubber: "scrubber_active",
    Store_Mode_N2: "n2_active",
};
const STORE_SWITCHES = new Set<StoreBlockFieldKey>(["Store_Mode_Scrubber", "Store_Mode_N2"]);

export function buildStoreEditPayload(draft: StoreDraft): ProgramEditPayload {
    const store: StoreEditPayload = {};
    (Object.entries(draft) as Array<[StoreBlockFieldKey, number]>).forEach(([key, value]) => {
        (store as Record<string, number | boolean>)[STORE_PAYLOAD_FIELDS[key]] = STORE_SWITCHES.has(key) ? value === 1 : value;
    });
    return { phases: [], store };
}

/** Plain-language lines describing what the store draft changes. */
export function describeStoreDraft(draft: StoreDraft, store: StoreValues): string[] {
    const lines = (Object.entries(draft) as Array<[StoreBlockFieldKey, number]>).map(([key, value]) => (
        `${storeFieldLabel(key)}: ${formatProgramFieldValue(key, store[key])} → ${formatProgramFieldValue(key, value)}`
    ));
    const before = ethyleneDosingFor(store.Store_Spv_C2H4);
    const after = ethyleneDosingFor(draftedStore(store, draft).Store_Spv_C2H4);
    if (before !== after) lines.push(`Ethylene dosing ${after ? "on" : "off"}`);
    return lines;
}
